import {
  ArrowDownAZ,
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  CalendarClock,
  LayoutList,
} from "lucide-react";
import type { SortPreference } from "../lib/sorting";

interface SortControlProps {
  value: SortPreference;
  onChange: (pref: SortPreference) => void;
  legend: string;
}

const baseButtonClass =
  "flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition";
const activeClass = "bg-blue-600 text-white dark:bg-blue-500";
const inactiveClass =
  "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600";

// Segmented control for choosing how a list is sorted: grouped by category,
// alphabetically, or by date added (toggling newest/oldest first on repeat clicks).
export default function SortControl({
  value,
  onChange,
  legend,
}: SortControlProps) {
  const handleDateClick = () => {
    onChange({
      mode: "date",
      dateDirection:
        value.mode === "date"
          ? value.dateDirection === "desc"
            ? "asc"
            : "desc"
          : value.dateDirection,
    });
  };

  const dateLabel =
    value.mode === "date"
      ? value.dateDirection === "desc"
        ? "Sorted by date added, newest first. Activate to sort oldest first."
        : "Sorted by date added, oldest first. Activate to sort newest first."
      : "Sort by date added, newest first";

  return (
    <fieldset>
      <legend className="sr-only">{legend}</legend>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-md flex items-center gap-2 font-bold text-gray-800 dark:text-gray-100">
          <ArrowDownUp
            className="h-4 w-4 text-blue-500 dark:text-blue-400"
            aria-hidden="true"
          />
          Sort:
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange({ ...value, mode: "category" })}
          aria-pressed={value.mode === "category"}
          className={`${baseButtonClass} ${
            value.mode === "category" ? activeClass : inactiveClass
          }`}
        >
          <LayoutList className="h-4 w-4" aria-hidden="true" />
          By Category
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...value, mode: "alphabetical" })}
          aria-pressed={value.mode === "alphabetical"}
          className={`${baseButtonClass} ${
            value.mode === "alphabetical" ? activeClass : inactiveClass
          }`}
        >
          <ArrowDownAZ className="h-4 w-4" aria-hidden="true" />
          Alphabetical
        </button>
        <button
          type="button"
          onClick={handleDateClick}
          aria-pressed={value.mode === "date"}
          aria-label={dateLabel}
          title={dateLabel}
          className={`${baseButtonClass} ${
            value.mode === "date" ? activeClass : inactiveClass
          }`}
        >
          {value.mode === "date" ? (
            value.dateDirection === "desc" ? (
              <ArrowDownWideNarrow className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ArrowUpWideNarrow className="h-4 w-4" aria-hidden="true" />
            )
          ) : (
            <CalendarClock className="h-4 w-4" aria-hidden="true" />
          )}
          Date Added
        </button>
      </div>
    </fieldset>
  );
}
